import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Save, Loader2, Plus, X } from "lucide-react";
import axiosApi from "../server/service";
import toast from "react-hot-toast";


interface UserFormState {
  username: string;
  fullName: string;
  userRole: string;
  employeeType?: string;
  status: string;
  password?: string;
  location: string;
  confirmPassword?: string;
  permissions: {
    designMaster: boolean;
    vendorMaster: boolean;
    sizeMaster: boolean;
    fabricMaster: boolean;
    astarMaster: boolean;
    orderManagement: boolean;
    userAdmin: boolean;
  };
  groupMembers: { _id?: string; name: string }[];
}

const UserFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState<UserFormState>({
    username: "",
    fullName: "",
    userRole: "",
    employeeType: "",
    status: "Active",
    password: "",
    confirmPassword: "",
    location: "",
    permissions: {
      designMaster: false,
      vendorMaster: false,
      sizeMaster: false,
      fabricMaster: false,
      astarMaster: false,
      orderManagement: false,
      userAdmin: false,
    },
    groupMembers: [],
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof UserFormState, string>>
  >({});
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEditMode);

  // --- FETCH USER DATA IN EDIT MODE ---
  const [memberName, setMemberName] = useState("");

  useEffect(() => {
    if (isEditMode) {
      const fetchUserData = async () => {
        try {
          const { data } = await axiosApi.get(`/api/users/get-user/${id}`);


          setFormData((prevData) => ({
            ...prevData,
            ...data.user,
            permissions: {
              designMaster: data.user.permissions?.designMaster || false,
              vendorMaster: data.user.permissions?.vendorMaster || false,
              sizeMaster: data.user.permissions?.sizeMaster || false,
              fabricMaster: data.user.permissions?.fabricMaster || false,
              astarMaster: data.user.permissions?.astarMaster || false,
              orderManagement: data.user.permissions?.orderManagement || false,
              userAdmin: data.user.permissions?.userAdmin || false,
            },
            groupMembers: data.user.groupMembers || []
          }));
        } catch (error) {
          toast.error("Failed to fetch user data.");
          
          navigate("/usermanagement");
        } finally {
          setPageLoading(false);
        }
      };
      fetchUserData();
    }
  }, [id, isEditMode, navigate]);

  const handleAddMember = () => {
    if (!memberName.trim()) {
      toast.error("Member name cannot be empty.");
      return;
    }
    // Check for duplicates
    if (formData.groupMembers.some(m => m.name.toLowerCase() === memberName.trim().toLowerCase())) {
        toast.error("This member name has already been added.");
        return;
    }
    setFormData((prev) => ({
      ...prev,
      groupMembers: [...prev.groupMembers, { name: memberName.trim() }],
    }));
    setMemberName(""); 
  };

  const handleRemoveMember = (nameToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      groupMembers: prev.groupMembers.filter(
        (member) => member.name !== nameToRemove
      ),
    }));
  };


  const validateField = (name: string, value: string) => {
    let message = "";
    
    if (name === "username" && !value.trim()) message = "Username is required";
    if (name === "fullName" && !value.trim()) message = "Full name is required";
    if (name === "userRole" && !value) message = "Role is required";
    if (name === "location" && !value) message = "Location is required";
    if (name === "employeeType" && formData.userRole === "employee" && !value)

      message = "Employee type is required";
    if (!isEditMode) {
      if (name === "password" && (!value || value.length < 6))
        message = "Password must be at least 6 characters";
      if (name === "confirmPassword" && value !== formData.password)
        message = "Passwords do not match";
    }
    return message;
  };

  const validateForm = () => {
    const newErrors: Partial<Record<keyof UserFormState, string>> = {};
    const fieldsToValidate = [
      "username",
      "fullName",
      "userRole",
      "status",
      "password",
      "confirmPassword",
      "employeeType",
    ];

    if (formData.employeeType === 'Group Work' && formData.groupMembers.length === 0) {
          newErrors.groupMembers = 'Please add at least one group member.';
      }

    fieldsToValidate.forEach((key) => {
      const value = formData[key as keyof UserFormState];
      if (typeof value === "string") {
        const msg = validateField(key, value);
        if (msg) newErrors[key as keyof UserFormState] = msg;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // --- EVENT HANDLERS (No changes) ---
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof UserFormState]) {
      const errorMessage = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: errorMessage || undefined }));
    }
  };

  const handlePermissionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      permissions: { ...prev.permissions, [name]: checked },
    }));
  };

  // --- SUBMIT HANDLER (CREATE OR UPDATE) ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please Check the details.");
      return;
    }

    setLoading(true);
    try {
      if (isEditMode) {
        const { password, confirmPassword, ...payload } = formData;
        await axiosApi.put(`/api/users/update-user/${id}`, payload);
        toast.success("User updated successfully");
      } else {
        await axiosApi.post("/api/users/create-user", formData);
        toast.success("User created successfully");
      }

      navigate("/usermanagement");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "An error occurred.");
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
            {isEditMode ? "Edit User" : "Create New User"}
          </h1>
          <p className="text-gray-500 mt-1">
            {isEditMode
              ? "Update user account details and permissions"
              : "Configure a new user account"}
          </p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Link
            to="/usermanagement"
            className="w-full sm:w-auto text-center px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>
              {loading
                ? "Saving..."
                : isEditMode
                ? "Save Changes"
                : "Save User"}
            </span>
          </button>
        </div>
      </div>

    
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-6">
          User Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            {errors.username && (
              <p className="text-red-500 text-xs mt-1">{errors.username}</p>
            )}
          </div>

          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            {errors.fullName && (
              <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>
            )}
          </div>

          {/* User Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              User Role
            </label>
            <select
              name="userRole"
              value={formData.userRole}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Select role</option>
              <option value="admin">Admin</option>
              <option value="supervisor">Supervisor</option>
              <option value="employee">Employee</option>
            </select>
            {errors.userRole && (
              <p className="text-red-500 text-xs mt-1">{errors.userRole}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Location
            </label>
            <select
              name="location"
              value={formData.location}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Select Location</option>
              <option value="Mumbai">Mumbai</option>
              <option value="Surat">Surat</option>
            </select>
            {errors.location && (
              <p className="text-red-500 text-xs mt-1">{errors.location}</p>
            )}
          </div>

          {/*  employee type */}
          {formData.userRole === "employee" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employee Type
              </label>
              <select
                name="employeeType"
                value={formData.employeeType || ""}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required // Enforces selection when visible
              >
                <option value="">Select type</option>
                <option value="Karigar">Karigar</option>
                <option value="Master">Master</option>
                <option value="job-work">Job Work</option>
                <option value="Group Work">Group Work</option>
              </select>
              {errors.employeeType && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.employeeType}
                </p>
              )}
            </div>
          )}

          {formData.employeeType === "Group Work" && (
            <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Group Members</h3>
                <div className="flex items-center gap-2 mb-4">
                    <input
                        type="text"
                        placeholder="Enter member name"
                        value={memberName}
                        onChange={(e) => setMemberName(e.target.value)}
                        className="flex-grow px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <button
                        type="button"
                        onClick={handleAddMember}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                    >
                        <Plus className="w-4 h-4" />
                        <span>Add</span>
                    </button>
                </div>

                {errors.groupMembers && <p className="text-red-500 text-xs mb-2">{errors.groupMembers}</p>}

                <div className="space-y-2">
                    {formData.groupMembers.length > 0 ? (
                        formData.groupMembers.map((member, index) => (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-md">
                            <span className="text-gray-700">{member.name}</span>
                            <button
                                type="button"
                                onClick={() => handleRemoveMember(member.name)}
                                className="p-1 text-red-500 hover:text-red-700"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        ))
                    ) : (
                        <p className="text-sm text-gray-500 text-center py-2">No members added yet.</p>
                    )}
                </div>
            </div>
        )}

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          {!isEditMode && (
            <>
              <div></div> {/* Alignment */}
              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password || ""}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                )}
              </div>
              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword || ""}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
                {errors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {(formData.userRole === "admin" ||
        formData.userRole === "supervisor") && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          {/* <h2 className="text-xl font-bold text-gray-800 mb-4">
            Access Permissions
          </h2> */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">
              Access Permissions
            </h2>

            {formData.userRole === "admin" && (
              <button
                type="button"
                onClick={() => {
                  const allSelected = Object.values(formData.permissions).every(
                    Boolean
                  );
                  setFormData((prev) => ({
                    ...prev,
                    permissions: {
                      designMaster: !allSelected,
                      vendorMaster: !allSelected,
                      sizeMaster: !allSelected,
                      fabricMaster: !allSelected,
                      astarMaster: !allSelected,
                      orderManagement: !allSelected,
                      userAdmin: !allSelected,
                    },
                  }));
                }}
                className="px-3 py-1 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
              >
                {Object.values(formData.permissions).every(Boolean)
                  ? "Unselect All"
                  : "Select All"}
              </button>
            )}
          </div>

          <div className="space-y-3">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                name="designMaster"
                checked={formData.permissions.designMaster}
                onChange={handlePermissionChange}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span>Design Master</span>
            </label>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                name="vendorMaster"
                checked={formData.permissions.vendorMaster}
                onChange={handlePermissionChange}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span>Vendor Master</span>
            </label>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                name="sizeMaster"
                checked={formData.permissions.sizeMaster}
                onChange={handlePermissionChange}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span>Size Master</span>
            </label>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                name="fabricMaster"
                checked={formData.permissions.fabricMaster}
                onChange={handlePermissionChange}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span>Fabric Type Master</span>
            </label>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                name="astarMaster"
                checked={formData.permissions.astarMaster}
                onChange={handlePermissionChange}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span>Astar Master</span>
            </label>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                name="orderManagement"
                checked={formData.permissions.orderManagement}
                onChange={handlePermissionChange}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span>Order Management</span>
            </label>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                name="userAdmin"
                checked={formData.permissions.userAdmin}
                onChange={handlePermissionChange}
                disabled={formData.userRole === "supervisor"}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span
                className={`select-none ${
                  formData.userRole === "supervisor"
                    ? "text-gray-400 cursor-not-allowed"
                    : ""
                }`}
              >
                User Management (Admin Only)
              </span>
            </label>
          </div>
        </div>
      )}
    </form>
  );
};

export default UserFormPage;
